import React, { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { API_URL, formatDate } from '../../lib/utils';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { useAuth } from '../../context/AuthContext';
import ShareButton from '../../components/ShareButton';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import { MessageSquare, Eye, ThumbsUp, Award, Flag, Lock, ShieldAlert, ArrowLeft, UserRound, Camera } from 'lucide-react';

const CommunityQuestion = () => {
  const { questionId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [answerBody, setAnswerBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showIdentity, setShowIdentity] = useState(false);
  const [hasAvatar, setHasAvatar] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileRef = React.useRef(null);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportTarget, setReportTarget] = useState(null);
  const [reportReason, setReportReason] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/community/questions/${questionId}`);
      setData(res.data);
      document.title = `${res.data.question?.title || 'Question'} | Ask a Social Worker`;
    } catch (e) {
      setData(null);
    } finally { setLoading(false); }
  }, [questionId]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isAuthenticated) {
      axios.get(`${API_URL}/community/profile-photo/me`).then((r) => setHasAvatar(!!r.data.has_avatar)).catch(() => {});
    }
  }, [isAuthenticated]);

  const uploadAvatar = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append('file', f);
    setUploadingAvatar(true);
    try {
      await axios.post(`${API_URL}/community/profile-photo`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setHasAvatar(true); setShowIdentity(true);
      toast.success('Profile photo saved.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not upload photo.');
    } finally { setUploadingAvatar(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  // If a shared link can't be resolved (unpublished/removed/expired), send people
  // back to the Ask a Social Worker page so the conversation can continue on-site.
  useEffect(() => {
    if (!loading && !data) {
      const t = setTimeout(() => navigate('/community/ask-a-social-worker', { replace: true }), 2500);
      return () => clearTimeout(t);
    }
  }, [loading, data, navigate]);

  const submitAnswer = async () => {
    if (!isAuthenticated) { navigate('/login'); return; }
    if (answerBody.trim().length < 5) { toast.error('Please write a longer response.'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/community/questions/${questionId}/answers`, { body: answerBody, show_identity: showIdentity });
      if (res.data.emergency) {
        toast.warning('If you or someone is in crisis, call or text 988 now.');
      }
      toast.success(res.data.message);
      setAnswerBody('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not post your response.');
    } finally { setSubmitting(false); }
  };

  const toggleHelpful = async (answerId) => {
    if (!isAuthenticated) { navigate('/login'); return; }
    try { await axios.post(`${API_URL}/community/answers/${answerId}/helpful`); load(); } catch (e) { /* noop */ }
  };

  const selectMostHelpful = async (answerId) => {
    try { const r = await axios.post(`${API_URL}/community/answers/${answerId}/most-helpful`); toast.success(r.data.message); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Action failed.'); }
  };

  const openReport = (type, id) => {
    if (!isAuthenticated) { navigate('/login'); return; }
    setReportTarget({ type, id }); setReportReason(''); setReportOpen(true);
  };

  const submitReport = async () => {
    try {
      await axios.post(`${API_URL}/community/report`, { target_type: reportTarget.type, target_id: reportTarget.id, reason: reportReason });
      toast.success('Thank you. This content was sent to the moderation team.');
      setReportOpen(false);
    } catch (e) { toast.error('Could not submit report.'); }
  };

  if (loading) return <div className="min-h-screen pt-28 text-center text-gray-500">Loading…</div>;
  if (!data) return (
    <div className="min-h-screen pt-28 text-center px-4">
      <p className="text-gray-600 font-medium">This question isn't available.</p>
      <p className="text-gray-400 text-sm mt-1">It may still be awaiting moderator review. Taking you to the community…</p>
      <Link to="/community/ask-a-social-worker"><Button className="mt-4 bg-secondary hover:bg-secondary-hover text-white">Go to Ask a Social Worker</Button></Link>
    </div>
  );

  const { question, answers, locked } = data;
  const isAuthor = user && question.author_id === user.user_id;

  return (
    <div className="min-h-screen bg-[#F7F5EF] pb-16">
      <section className="bg-primary pt-24 pb-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-white">
          <button onClick={() => navigate('/community/questions')} className="text-secondary text-sm hover:underline flex items-center gap-1"><ArrowLeft className="w-4 h-4" /> All Questions</button>
          <span className="inline-block text-xs font-semibold text-secondary uppercase tracking-wide mt-3">{question.category}</span>
          <h1 className="font-serif text-2xl sm:text-3xl font-bold mt-1" data-testid="question-title">{question.title}</h1>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-3 text-xs text-white/70">
            <span>{question.author_display}</span>
            {question.state && <span>{question.state}</span>}
            {question.published_at && <span>{formatDate(question.published_at)}</span>}
            <span className="flex items-center gap-1"><MessageSquare className="w-3.5 h-3.5" /> {answers.length}</span>
            <span className="flex items-center gap-1"><Eye className="w-3.5 h-3.5" /> {question.views}</span>
            {locked && <span className="flex items-center gap-1 text-secondary"><Lock className="w-3.5 h-3.5" /> Locked</span>}
          </div>
          <div className="mt-4">
            <ShareButton path={`/community/questions/${question.question_id}`} title={question.title}
              variant="outline" className="border-white text-white hover:bg-white hover:text-primary h-9"
              label="Share this question" testid="share-question-btn" />
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-4">
        <div className="bg-white rounded-2xl border p-6">
          <p className="text-gray-700 whitespace-pre-wrap" data-testid="question-body">{question.body}</p>
          {question.tags?.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-4">
              {question.tags.map((t) => <span key={t} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">#{t}</span>)}
            </div>
          )}
          <div className="mt-4 pt-3 border-t flex justify-end">
            <button onClick={() => openReport('question', question.question_id)} className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1" data-testid="report-question-btn"><Flag className="w-3.5 h-3.5" /> Report</button>
          </div>
        </div>

        {/* Disclaimer */}
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 text-sm text-amber-800">
          <ShieldAlert className="w-5 h-5 flex-shrink-0 text-amber-600" />
          <p>Responses are peer support from fellow social workers — not legal or clinical advice. Never share client-identifying details. In an emergency, call or text <strong>988</strong> or your local emergency services.</p>
        </div>

        {/* Answers */}
        <h2 className="font-serif text-xl font-bold text-primary mt-8 mb-3">{answers.length} Response{answers.length === 1 ? '' : 's'}</h2>
        <div className="space-y-3">
          {answers.map((a) => (
            <div key={a.answer_id} className={`bg-white rounded-2xl border p-5 ${a.is_most_helpful ? 'border-secondary ring-1 ring-secondary/40' : ''}`} data-testid={`answer-${a.answer_id}`}>
              {a.is_most_helpful && <span className="inline-flex items-center gap-1 text-xs font-semibold text-secondary mb-2"><Award className="w-3.5 h-3.5" /> Most Helpful Answer</span>}
              <div className="flex items-start gap-3">
                {a.author_user_id ? (
                  <img src={`${API_URL}/community/profile-photo/${a.author_user_id}`} alt={a.author_display} className="w-9 h-9 rounded-full object-cover border flex-shrink-0" data-testid={`answer-avatar-${a.answer_id}`} onError={(e) => { e.target.style.display = 'none'; }} />
                ) : (
                  <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0"><UserRound className="w-4 h-4 text-primary" /></div>
                )}
                <p className="text-gray-700 whitespace-pre-wrap flex-1">{a.body}</p>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-2 mt-4 pt-3 border-t">
                <span className="text-xs text-gray-400">{a.author_display} · {formatDate(a.created_at)}</span>
                <div className="flex items-center gap-3">
                  <button onClick={() => toggleHelpful(a.answer_id)} className="text-xs text-gray-500 hover:text-primary flex items-center gap-1" data-testid={`helpful-btn-${a.answer_id}`}><ThumbsUp className="w-3.5 h-3.5" /> Helpful ({a.helpful_count || 0})</button>
                  {isAuthor && !a.is_most_helpful && (
                    <button onClick={() => selectMostHelpful(a.answer_id)} className="text-xs text-secondary hover:underline flex items-center gap-1" data-testid={`most-helpful-btn-${a.answer_id}`}><Award className="w-3.5 h-3.5" /> Mark Most Helpful</button>
                  )}
                  <button onClick={() => openReport('answer', a.answer_id)} className="text-xs text-gray-400 hover:text-red-500 flex items-center gap-1"><Flag className="w-3.5 h-3.5" /> Report</button>
                </div>
              </div>
            </div>
          ))}
          {answers.length === 0 && <div className="bg-white rounded-2xl border p-6 text-center text-gray-500 text-sm">No responses yet. Be the first to share your insight.</div>}
        </div>

        {/* Answer form */}
        {locked ? (
          <div className="mt-6 bg-gray-100 rounded-xl p-5 text-center text-gray-500 text-sm flex items-center justify-center gap-2"><Lock className="w-4 h-4" /> This discussion is locked. New responses are closed.</div>
        ) : (
          <div className="mt-6 bg-white rounded-2xl border p-5">
            <h3 className="font-semibold text-primary mb-2">What would you do in this situation? Share your professional perspective below.</h3>
            {isAuthenticated ? (
              <>
                <Textarea rows={5} placeholder="Offer your professional insight. Keep it respectful and never include client-identifying information." value={answerBody} onChange={(e) => setAnswerBody(e.target.value)} data-testid="answer-input" />
                <p className="text-xs text-gray-400 mt-2">Responses are auto-screened for safety. Content mentioning emergencies, legal advice, or possible client details is held for moderator review.</p>

                {/* Identity / photo choice */}
                <div className="mt-3 bg-[#F7F5EF] rounded-lg p-3">
                  <p className="text-sm font-medium text-primary mb-2">How would you like to appear?</p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button type="button" onClick={() => setShowIdentity(false)} data-testid="identity-anon-btn"
                      className={`flex-1 text-sm rounded-lg border px-3 py-2 text-left ${!showIdentity ? 'border-primary bg-white ring-1 ring-primary/30' : 'border-gray-200 bg-white'}`}>
                      <span className="font-semibold">Post anonymously</span>
                      <span className="block text-xs text-gray-500">Shown as "Anonymous Social Worker"</span>
                    </button>
                    <button type="button" onClick={() => setShowIdentity(true)} data-testid="identity-photo-btn"
                      className={`flex-1 text-sm rounded-lg border px-3 py-2 text-left ${showIdentity ? 'border-primary bg-white ring-1 ring-primary/30' : 'border-gray-200 bg-white'}`}>
                      <span className="font-semibold">Post with my name{hasAvatar ? ' & photo' : ''}</span>
                      <span className="block text-xs text-gray-500">{hasAvatar ? 'Your profile photo will show' : 'Add a photo below (optional)'}</span>
                    </button>
                  </div>
                  {showIdentity && (
                    <div className="flex items-center gap-3 mt-3">
                      {hasAvatar ? (
                        <img src={`${API_URL}/community/profile-photo/${user.user_id}`} alt="Your profile" className="w-10 h-10 rounded-full object-cover border" data-testid="my-avatar-preview" />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary"><UserRound className="w-5 h-5" /></div>
                      )}
                      <input ref={fileRef} type="file" accept="image/*" onChange={uploadAvatar} className="hidden" data-testid="avatar-file-input" />
                      <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploadingAvatar} data-testid="upload-avatar-btn">
                        <Camera className="w-4 h-4 mr-1" /> {uploadingAvatar ? 'Uploading…' : hasAvatar ? 'Change photo' : 'Upload profile photo'}
                      </Button>
                    </div>
                  )}
                </div>

                <div className="flex justify-end mt-3">
                  <Button onClick={submitAnswer} disabled={submitting} className="bg-secondary hover:bg-secondary-hover text-white" data-testid="submit-answer-btn">{submitting ? 'Posting…' : 'Post Response'}</Button>
                </div>
              </>
            ) : (
              <div className="text-center py-4">
                <p className="text-gray-600 text-sm">Sign in to share your response with the community.</p>
                <Button onClick={() => navigate('/login')} className="mt-3 bg-secondary hover:bg-secondary-hover text-white" data-testid="answer-login-btn">Sign In</Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Report dialog */}
      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle>Report content</DialogTitle>
            <DialogDescription>Tell our moderators why this content should be reviewed.</DialogDescription>
          </DialogHeader>
          <Textarea rows={4} placeholder="Reason (e.g. shares client info, offensive, spam, legal/clinical advice)" value={reportReason} onChange={(e) => setReportReason(e.target.value)} data-testid="report-reason-input" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportOpen(false)}>Cancel</Button>
            <Button onClick={submitReport} disabled={!reportReason.trim()} className="bg-red-600 hover:bg-red-700 text-white" data-testid="submit-report-btn">Submit Report</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CommunityQuestion;
